/* biome-ignore-all lint/suspicious/noExplicitAny: reading content detail modal */
/* biome-ignore-all lint/suspicious/noArrayIndexKey: question and answer list */
import type React from "react"
import { useEffect, useMemo } from "react"
import { Modal, Tag, Typography, Button, Space, Divider, Empty, Badge } from "antd"
import { LeftOutlined, RightOutlined, BookOutlined, QuestionCircleOutlined, LinkOutlined } from "@ant-design/icons"
import { IconVocabulary, IconBulb, IconFileText } from "@tabler/icons-react"
import type { ReadingContentItem } from "../types"
import "@/styles/table.full.css"

const { Paragraph } = Typography

export const DIFFICULTY_MAP: Record<string, { label: string; color: string }> = {
  easy: { label: "Dễ (Easy)", color: "green" },
  medium: { label: "Trung bình (Medium)", color: "gold" },
  hard: { label: "Khó (Hard)", color: "volcano" },
}

export const STATUS_MAP: Record<string, { label: string; color: string }> = {
  ready: { label: "Sẵn sàng", color: "success" },
  draft: { label: "Bản nháp", color: "default" },
  archived: { label: "Lưu trữ", color: "warning" },
}

export interface ReadingContentDetailModalProps {
  open: boolean
  record: ReadingContentItem | null
  items?: ReadingContentItem[]
  currentIndex?: number
  onNavigate?: (index: number) => void
  onClose: () => void
}

export const ReadingContentDetailModal: React.FC<ReadingContentDetailModalProps> = ({
  open,
  record,
  items = [],
  currentIndex = 0,
  onNavigate,
  onClose,
}) => {
  const totalRecords = items.length
  const hasPrev = typeof currentIndex === "number" && currentIndex > 0
  const hasNext = typeof currentIndex === "number" && currentIndex < totalRecords - 1

  const handlePrev = () => {
    if (hasPrev && onNavigate) {
      onNavigate(currentIndex - 1)
    }
  }

  const handleNext = () => {
    if (hasNext && onNavigate) {
      onNavigate(currentIndex + 1)
    }
  }

  // Keyboard shortcut: ArrowLeft / ArrowRight to navigate
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const tagName = target?.tagName?.toUpperCase()
      if (tagName === "INPUT" || tagName === "TEXTAREA" || target?.isContentEditable) {
        return
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault()
        handlePrev()
      } else if (e.key === "ArrowRight") {
        e.preventDefault()
        handleNext()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [open, hasPrev, hasNext, currentIndex, onNavigate])

  const diffInfo = record?.difficulty ? DIFFICULTY_MAP[record.difficulty] : null
  const statusInfo = record?.status ? STATUS_MAP[record.status] : null
  const setInfo = record?.set

  const setUrl = setInfo?.id
    ? setInfo.slug
      ? `https://jupi.vn/set/${setInfo.slug}-${setInfo.id}`
      : `https://jupi.vn/set/${setInfo.id}`
    : null

  // Highlight passage text with target vocabularies
  const highlightedPassage = useMemo(() => {
    if (!record?.passage_text) return null
    const vocabList = record.target_vocabularies || []
    if (!vocabList.length) {
      return (
        <Paragraph className="whitespace-pre-line text-gray-800 text-sm leading-relaxed">
          {record.passage_text}
        </Paragraph>
      )
    }

    // Escape regex characters
    const escapedVocabs = vocabList
      .filter(Boolean)
      .map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .sort((a, b) => b.length - a.length)

    if (!escapedVocabs.length) {
      return (
        <Paragraph className="whitespace-pre-line text-gray-800 text-sm leading-relaxed">
          {record.passage_text}
        </Paragraph>
      )
    }

    const regex = new RegExp(`\\b(${escapedVocabs.join("|")})\\b`, "gi")
    const parts = record.passage_text.split(regex)

    return (
      <div className="whitespace-pre-line font-sans text-gray-800 text-sm leading-relaxed">
        {parts.map((part, i) => {
          const isTarget = vocabList.some((v) => v.toLowerCase() === part.toLowerCase())
          if (isTarget) {
            return (
              <mark
                key={i}
                className="rounded border border-amber-200 bg-amber-100 px-1 py-0.5 font-semibold text-amber-900"
              >
                {part}
              </mark>
            )
          }
          return part
        })}
      </div>
    )
  }, [record?.passage_text, record?.target_vocabularies])

  const questions = record?.questions || {}
  const clozeQuestions = questions.cloze || []
  const mcqQuestions = questions.mcq || []
  const tfQuestions = questions.tf || []
  const totalQuestions = clozeQuestions.length + mcqQuestions.length + tfQuestions.length

  const wordCount = useMemo(() => {
    if (!record?.passage_text) return 0
    return record.passage_text.split(/\s+/).filter(Boolean).length
  }, [record?.passage_text])

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="96vw"
      style={{
        top: 10,
        margin: "0 auto",
        padding: 0,
        maxWidth: "1600px",
        height: "calc(100vh - 20px)",
      }}
      styles={{
        wrapper: {
          overflow: "hidden",
        },
        container: {
          height: "calc(100vh - 20px)",
          maxHeight: "calc(100vh - 20px)",
          display: "flex",
          flexDirection: "column",
          padding: "16px 20px 12px 20px",
          overflow: "hidden",
        },
        header: {
          margin: 0,
          padding: 0,
          flexShrink: 0,
        },
        body: {
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
          padding: "12px 0 6px 0",
          display: "flex",
          flexDirection: "column",
        },
        footer: {
          margin: 0,
          padding: 0,
          flexShrink: 0,
        },
      }}
      title={
        <div className="flex flex-wrap items-center justify-between gap-3 border-gray-200 border-b pr-6 pb-2">
          <div className="flex items-center gap-2">
            <span className="font-bold text-gray-800 text-lg">
              📖 Bài đọc #{record?.id} - Part {record?.part_number ?? 1}
            </span>
            {record?.title && (
              <span className="max-w-[240px] truncate font-semibold text-gray-700 text-sm" title={record.title}>
                ({record.title})
              </span>
            )}
            {diffInfo && (
              <Tag color={diffInfo.color} className="font-medium text-xs">
                {diffInfo.label}
              </Tag>
            )}
            {statusInfo && (
              <Tag color={statusInfo.color} className="font-medium text-xs">
                {statusInfo.label}
              </Tag>
            )}

            {totalRecords > 1 && typeof currentIndex === "number" && (
              <Space size={4} className="ml-2">
                <Button
                  size="small"
                  icon={<LeftOutlined />}
                  onClick={handlePrev}
                  disabled={!hasPrev}
                  title="Bài trước (Phím ←)"
                >
                  Trước
                </Button>
                <span className="px-1 font-medium font-mono text-gray-500 text-xs">
                  {currentIndex + 1} / {totalRecords}
                </span>
                <Button
                  size="small"
                  icon={<RightOutlined />}
                  onClick={handleNext}
                  disabled={!hasNext}
                  title="Bài tiếp theo (Phím →)"
                >
                  Tiếp
                </Button>
              </Space>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs">
            {setInfo && (
              <div className="flex items-center gap-1.5">
                <span className="text-gray-500">Bộ từ:</span>
                {setUrl ? (
                  <a
                    href={setUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 font-semibold text-primary hover:underline"
                  >
                    <span>{setInfo.full_title || setInfo.title}</span>
                    <LinkOutlined className="text-[10px]" />
                  </a>
                ) : (
                  <strong className="text-gray-800">{setInfo.full_title || setInfo.title}</strong>
                )}
                <Tag color={setInfo.type === "system" ? "blue" : "orange"} className="m-0 text-[10px]">
                  {setInfo.type === "system" ? "Hệ thống" : "Người dùng"}
                </Tag>
              </div>
            )}

            {record?.genre && (
              <>
                <Divider orientation="vertical" className="m-0" />
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Thể loại:</span>
                  <Tag className="m-0 text-xs">{record.genre}</Tag>
                </div>
              </>
            )}

            {record?.model && (
              <>
                <Divider orientation="vertical" className="m-0" />
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Model AI:</span>
                  <span className="font-mono text-gray-700">{record.model}</span>
                </div>
              </>
            )}
          </div>
        </div>
      }
      footer={
        <div className="flex items-center justify-between border-gray-200 border-t pt-2">
          <div className="flex items-center gap-4 font-mono text-gray-500 text-xs">
            <span>Bài đọc ID: #{record?.id || "—"}</span>
            <span>Tổng câu hỏi: {totalQuestions} câu</span>
            <span>Số từ đoạn văn: {wordCount} từ</span>
          </div>
          <Button type="primary" onClick={onClose}>
            Đóng
          </Button>
        </div>
      }
    >
      {/* 2-COLUMN SPLIT VIEW */}
      <div className="grid h-full min-h-0 grid-cols-1 gap-3 md:grid-cols-2">
        {/* LEFT COLUMN: PASSAGE TEXT & TARGET VOCABULARIES */}
        <div className="flex h-full min-h-0 flex-col rounded-lg border border-gray-200 bg-gray-50/50 p-2.5 shadow-xs">
          {/* Header Left */}
          <div className="mb-2 flex items-center justify-between border-gray-200 border-b pb-2">
            <div className="flex items-center gap-2">
              <BookOutlined className="text-base text-blue-500" />
              <span className="font-bold text-gray-800 text-sm">📖 ĐOẠN VĂN BÀI ĐỌC (PASSAGE)</span>
              <Tag color="cyan" className="m-0 text-xs">
                Part {record?.part_number ?? 1}
              </Tag>
            </div>
            {record?.title && (
              <span className="max-w-[200px] truncate font-semibold text-gray-700 text-xs" title={record.title}>
                {record.title}
              </span>
            )}
          </div>

          {/* Left Content Scrollable */}
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
            {/* Passage Text Card */}
            <div className="rounded border border-gray-200 bg-white p-3.5 shadow-2xs">
              <div className="mb-2.5 flex items-center justify-between border-gray-100 border-b pb-1.5">
                <span className="flex items-center gap-1.5 font-semibold text-gray-700 text-xs">
                  <IconFileText size={16} className="text-blue-500" />
                  Nội dung bài đọc (Đoạn văn)
                </span>
                <span className="text-[11px] text-gray-400">{wordCount} từ</span>
              </div>
              {highlightedPassage || <Empty description="Chưa có nội dung đoạn văn bài đọc" />}
            </div>

            {/* Target Vocabularies */}
            {record?.target_vocabularies && record.target_vocabularies.length > 0 && (
              <div className="rounded border border-gray-200 bg-white p-3 shadow-2xs">
                <div className="mb-2 flex items-center gap-1.5 font-semibold text-gray-700 text-xs">
                  <IconVocabulary size={16} className="text-amber-500" />
                  Từ vựng mục tiêu (Target Vocabularies - {record.target_vocabularies.length} từ)
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {record.target_vocabularies.map((vocab, idx) => (
                    <Tag key={idx} color="gold" className="m-0 border-amber-300 px-2 py-0.5 font-medium text-xs">
                      {vocab}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            {/* Word Bank */}
            {record?.word_bank && record.word_bank.length > 0 && (
              <div className="rounded border border-gray-200 bg-white p-3 shadow-2xs">
                <div className="mb-2 flex items-center gap-1.5 font-semibold text-gray-700 text-xs">
                  <IconBulb size={16} className="text-purple-500" />
                  Kho từ gợi ý (Word Bank - {record.word_bank.length} từ)
                </div>
                <div className="flex flex-wrap gap-1">
                  {record.word_bank.map((w, idx) => (
                    <Tag key={idx} color="purple" className="m-0 text-xs">
                      {w}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: QUESTIONS & ANSWER KEY */}
        <div className="flex h-full min-h-0 flex-col rounded-lg border border-gray-200 bg-gray-50/50 p-2.5 shadow-xs">
          {/* Header Right */}
          <div className="mb-2 flex items-center justify-between border-gray-200 border-b pb-2">
            <div className="flex items-center gap-2">
              <QuestionCircleOutlined className="text-blue-500" />
              <span className="font-bold text-gray-800 text-sm">
                📝 NỘI DUNG CÂU HỎI & ĐÁP ÁN ({totalQuestions} câu)
              </span>
            </div>
            <div className="flex items-center gap-1 text-[11px]">
              {clozeQuestions.length > 0 && (
                <Tag color="blue" className="m-0">
                  {clozeQuestions.length} Điền từ
                </Tag>
              )}
              {mcqQuestions.length > 0 && (
                <Tag color="purple" className="m-0">
                  {mcqQuestions.length} Trắc nghiệm
                </Tag>
              )}
              {tfQuestions.length > 0 && (
                <Tag color="cyan" className="m-0">
                  {tfQuestions.length} Đúng/Sai
                </Tag>
              )}
            </div>
          </div>

          {/* Right Content Scrollable */}
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
            {/* 1. Cloze Questions */}
            {clozeQuestions.length > 0 && (
              <div className="rounded border border-gray-200 bg-white p-3 shadow-2xs">
                <span className="mb-2 inline-block rounded bg-blue-50 px-2 py-0.5 font-semibold text-blue-700 text-xs">
                  1. Điền từ vào chỗ trống (Cloze - {clozeQuestions.length} câu)
                </span>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {clozeQuestions.map((q, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 p-2 text-xs"
                    >
                      <Badge count={q.index ?? idx + 1} style={{ backgroundColor: "#1890ff" }} />
                      <div className="flex flex-col">
                        <span className="font-semibold text-emerald-700">Đáp án chuẩn: {q.correct_answer}</span>
                        {q.accepted_answers && q.accepted_answers.length > 0 && (
                          <span className="text-[11px] text-gray-500">Chấp nhận: {q.accepted_answers.join(", ")}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 2. Multiple Choice Questions (MCQ) */}
            {mcqQuestions.length > 0 && (
              <div className="rounded border border-gray-200 bg-white p-3 shadow-2xs">
                <span className="mb-2 inline-block rounded bg-purple-50 px-2 py-0.5 font-semibold text-purple-700 text-xs">
                  2. Trắc nghiệm (MCQ - {mcqQuestions.length} câu)
                </span>
                <div className="flex flex-col gap-2.5">
                  {mcqQuestions.map((q, idx) => (
                    <div
                      key={idx}
                      className="flex flex-col gap-1.5 rounded border border-gray-200 bg-gray-50 p-2.5 text-xs"
                    >
                      <div className="font-semibold text-gray-800">
                        Câu {idx + 1}: {q.question}
                      </div>

                      <div className="grid grid-cols-1 gap-1 pl-2 sm:grid-cols-2">
                        {q.options?.map((opt) => {
                          const isCorrect = opt.key.toLowerCase() === q.answer?.toLowerCase()
                          return (
                            <div
                              key={opt.key}
                              className={`rounded border px-2 py-1 text-[11px] ${
                                isCorrect
                                  ? "border-emerald-400 bg-emerald-100 font-semibold text-emerald-900 ring-1 ring-emerald-400"
                                  : "border-gray-200 bg-white text-gray-700"
                              }`}
                            >
                              <strong>{opt.key}.</strong> {opt.text}
                              {isCorrect && (
                                <Tag color="success" className="ml-1.5 px-1 py-0 text-[9px]">
                                  Đáp án đúng
                                </Tag>
                              )}
                            </div>
                          )
                        })}
                      </div>

                      {q.explanation && (
                        <div className="mt-1 rounded border border-blue-100 bg-blue-50/60 p-1.5 text-[11px] text-gray-600">
                          💡 <strong>Giải thích:</strong> {q.explanation}
                        </div>
                      )}

                      {q.evidence_snippets && q.evidence_snippets.length > 0 && (
                        <div className="pl-1 text-[11px] text-gray-600">
                          📌 <strong>Dẫn chứng:</strong>{" "}
                          {q.evidence_snippets.map((snip, sIdx) => {
                            if (typeof snip === "string") return <span key={sIdx}>“{snip}” </span>
                            return (
                              <span key={sIdx} className="text-gray-700 italic">
                                “{snip.en}” ({snip.vi}){" "}
                              </span>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 3. True / False Questions (TF) */}
            {tfQuestions.length > 0 && (
              <div className="rounded border border-gray-200 bg-white p-3 shadow-2xs">
                <span className="mb-2 inline-block rounded bg-cyan-50 px-2 py-0.5 font-semibold text-cyan-700 text-xs">
                  3. Đúng / Sai (True-False - {tfQuestions.length} câu)
                </span>
                <div className="flex flex-col gap-2">
                  {tfQuestions.map((q, idx) => (
                    <div
                      key={idx}
                      className="flex flex-col gap-1.5 rounded border border-gray-200 bg-gray-50 p-2.5 text-xs"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-semibold text-gray-800">
                          Câu {idx + 1}: {q.statement}
                        </span>
                        <Tag
                          color={String(q.answer).toLowerCase() === "true" ? "success" : "error"}
                          className="m-0 font-bold font-mono"
                        >
                          {String(q.answer).toUpperCase()}
                        </Tag>
                      </div>

                      {q.explanation && (
                        <div className="rounded border border-blue-100 bg-blue-50/60 p-1.5 text-[11px] text-gray-600">
                          💡 <strong>Giải thích:</strong> {q.explanation}
                        </div>
                      )}

                      {q.evidence_snippets && q.evidence_snippets.length > 0 && (
                        <div className="pl-1 text-[11px] text-gray-600">
                          📌 <strong>Dẫn chứng:</strong>{" "}
                          {q.evidence_snippets.map((snip, sIdx) => {
                            if (typeof snip === "string") return <span key={sIdx}>“{snip}” </span>
                            return (
                              <span key={sIdx} className="text-gray-700 italic">
                                “{snip.en}” ({snip.vi}){" "}
                              </span>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!clozeQuestions.length && !mcqQuestions.length && !tfQuestions.length && (
              <Empty description="Không có câu hỏi chi tiết trong bài đọc này" />
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default ReadingContentDetailModal
